import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useFloorPlanStore } from '../store/useFloorPlanStore'
import { FLOOR_PLAN_USE_API } from '../config'
import FloorPlanAPI from '../api/FloorPlanAPI'
import { QUERY } from '../api/FloorPlanAPI.model'
import { floorPlanToStore, serializePlan } from '../api/floorPlanMapper'
import { DEFAULT_ROOMS } from '../utils/defaults'
import { FloorTable, Room } from '../types/floorPlan.model'

/**
 * Hydrates the local store from the backend when FLOOR_PLAN_USE_API is enabled
 * and records the saved baseline used for dirty-tracking. When disabled it is a
 * no-op and the persisted local store is used as-is.
 */
export function useFloorPlanQuery() {
  // The API path is prefixed with this restaurant, so scope the cache to it too
  //, otherwise switching restaurants would reuse the previous plan.
  const restaurantId =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem('restaurantId')
      : null

  const { data, isLoading } = useQuery({
    queryKey: [QUERY.FLOOR_PLAN, restaurantId],
    enabled: FLOOR_PLAN_USE_API && !!restaurantId,
    queryFn: async () => floorPlanToStore(await FloorPlanAPI.getFloorPlan())
  })

  // Which restaurant the store currently holds a hydrated plan for.
  const hydratedForRef = useRef<string | null>(null)

  useEffect(() => {
    if (!FLOOR_PLAN_USE_API || !data) return

    const state = useFloorPlanStore.getState()
    // Only a genuine switch BETWEEN two restaurants discards local work. The very
    // first hydration (ref still null) must NOT count as a switch, otherwise a
    // fresh load with an empty backend would wipe the tables the user already
    // built locally and replace them with the default empty rooms.
    const restaurantChanged =
      hydratedForRef.current !== null &&
      hydratedForRef.current !== restaurantId

    // Within the same restaurant, never clobber unsaved local edits, e.g. when
    // navigating back into the planner and the query serves its cached result.
    const localDirty =
      state.savedSnapshot !== null &&
      serializePlan(state.rooms, state.tables) !== state.savedSnapshot
    if (!restaurantChanged && localDirty) return

    hydratedForRef.current = restaurantId

    const hasServerPlan = data.rooms.length > 0
    // Only carry local work forward within the SAME restaurant; on a switch the
    // previous restaurant's tables must not leak into this one.
    const keepLocalWork = !restaurantChanged && state.tables.length > 0

    let next: { rooms: Room[]; tables: FloorTable[] }
    if (hasServerPlan) {
      next = data // server plan wins when it exists
    } else if (keepLocalWork) {
      next = { rooms: state.rooms, tables: state.tables }
    } else {
      next = { rooms: DEFAULT_ROOMS.map(room => ({ ...room })), tables: [] }
    }

    // Baseline reflects what the SERVER currently holds, so kept-but-unsaved
    // local work shows as dirty (prompting a save), while a server-loaded or
    // fresh-default plan starts clean.
    let baseline: string
    if (hasServerPlan) {
      baseline = serializePlan(data.rooms, data.tables)
    } else if (keepLocalWork) {
      baseline = serializePlan([], []) // server is empty → local work is unsaved
    } else {
      baseline = serializePlan(next.rooms, next.tables)
    }

    useFloorPlanStore.setState({
      rooms: next.rooms,
      tables: next.tables,
      activeRoomId:
        state.activeRoomId &&
        next.rooms.some(room => room.id === state.activeRoomId)
          ? state.activeRoomId
          : next.rooms[0]?.id ?? null,
      savedSnapshot: baseline
    })
  }, [data, restaurantId])

  return { isLoading: FLOOR_PLAN_USE_API ? isLoading : false }
}
